import { useEffect, useState, useContext } from "react";
import ListAndAddTabs from "components/Layout/ListAndAddTabs";
import Page from "components/Page";
import { BuildingContext } from "Context/BuildingProvider";
import RolesTable from "Modules/Roles/RolesTable";
import StepperFrame, {
  StepFrame,
  InvalidationCheck,
} from "components/Layout/StepperFrame";
import ClickLocation from "components/Maps/ClickLocation";
import { GET_FLOOR_OVERLAYS } from "lib/buildings";
import {
  GetFloorOverlaysQueryResult,
  InsertRoleMutationVariables,
} from "generated/graphql";
import { useMutation, useQuery } from "@apollo/client";
import BasicRoleConfig from "Modules/Roles/BasicRoleConfig";
import RoleChecklists from "Modules/Roles/RoleChecklists";
import { INSERT_ROLE } from "lib/roles";
import { RoleTypeEnum } from "Modules/Roles/BasicRoleConfig";
import { AuthContext } from "Firebase/AuthProvider";

interface RolesState {
  editing: boolean;
  trackingType: "Indoor" | "Outdoor" | "None";
  type: RoleTypeEnum;
  name: string;
  lat: number;
  lng: number;
  floorNumber: number;
  selectedRoleTaskStarts: any[];
  selectedRoleTaskSends: any[];
  selectedPOIs: any[];
  tabValue: number;
}

const DEFAULT_STATE: RolesState = {
  editing: false,
  trackingType: "None",
  type: RoleTypeEnum.ROAM,
  name: "",
  lat: 0.0,
  lng: 0.0,
  tabValue: 0,
  floorNumber: 0,
  selectedRoleTaskStarts: [],
  selectedRoleTaskSends: [],
  selectedPOIs: [],
};

export default function Roles() {
  const [state, setState] = useState(DEFAULT_STATE);
  const { currentBuilding, updateBuilding } = useContext(BuildingContext);
  const { setRoute } = useContext(AuthContext);
  useEffect(() => {
    setRoute("Roles");
  }, []);
  const { loading, error, data }: GetFloorOverlaysQueryResult = useQuery(
    GET_FLOOR_OVERLAYS,
    {
      variables: {
        buildingId: currentBuilding?.Id,
      },
    }
  );
  const [insertRole] = useMutation<InsertRoleMutationVariables>(INSERT_ROLE);
  const onButtonClicked = () => {};

  const handleTaskStartClick = (id: number | string) => {
    if (state.selectedRoleTaskStarts.includes(id)) {
      const newSelectedRoleTaskStarts = state.selectedRoleTaskStarts.filter(
        (rts) => {
          return rts !== id;
        }
      );
      setState({ ...state, selectedRoleTaskStarts: newSelectedRoleTaskStarts });
    } else {
      const newSelectedRoleTaskStarts = state.selectedRoleTaskStarts;
      newSelectedRoleTaskStarts.push(id);

      setState({ ...state, selectedRoleTaskStarts: newSelectedRoleTaskStarts });
    }
  };

  const handleTaskSendClick = (id: number | string) => {
    if (state.selectedRoleTaskSends.includes(id)) {
      const newSelectedRoleTaskSends = state.selectedRoleTaskSends.filter(
        (rts) => {
          return rts !== id;
        }
      );
      setState({ ...state, selectedRoleTaskSends: newSelectedRoleTaskSends });
    } else {
      const newSelectedRoleTaskSends = state.selectedRoleTaskSends;
      newSelectedRoleTaskSends.push(id);
      setState({ ...state, selectedRoleTaskSends: newSelectedRoleTaskSends });
    }
  };

  const handlePOIClick = (id: number | string) => {
    if (state.selectedPOIs.includes(id)) {
      const newSelectedPOIs = state.selectedPOIs.filter((rts) => {
        return rts !== id;
      });
      setState({ ...state, selectedPOIs: newSelectedPOIs });
    } else {
      const newSelectedPOIs = state.selectedPOIs;
      newSelectedPOIs.push(id);
      setState({ ...state, selectedPOIs: newSelectedPOIs });
    }
  };

  const checkIfRoleConfigIsInvalid = (): InvalidationCheck => {
    let invalid = false;
    let message = "";
    if (state.name === "") {
      invalid = true;
      message = "Please add name for Role";
    } else if (state.trackingType === "None") {
      invalid = true;
      message = "Choose indoor or outdoor tracking for this role";
    } else {
      invalid = false;
    }
    return { invalid, message };
  };

  const stepFrames: StepFrame[] = [
    {
      label: "Name and Choose Role Type",
      inValid: checkIfRoleConfigIsInvalid(),
      children: (
        <BasicRoleConfig
          handleTrackingTypeChange={(trackingType) => {
            setState({ ...state, trackingType });
          }}
          handleRoleTypeChange={(type) => {
            setState({ ...state, type });
          }}
          handleRoleNameChange={(name) => {
            setState({ ...state, name });
          }}
        />
      ),
    },
    {
      label: "Set Standard Starting Point",
      children: loading ? (
        <div>loading</div>
      ) : error ? (
        <div>{error.message}</div>
      ) : (
        <ClickLocation
          floorOverlays={data!.FloorOverlay}
          onNewLocation={(lat: number, lng: number, floorNumber: number) => {
            setState({ ...state, lat, lng, floorNumber });
          }}
          mapId={"roleMap"}
        ></ClickLocation>
      ),
    },
    {
      label: "Assign Tasks",
      children: (
        <RoleChecklists
          handlePOIClick={(id: number | string) => {
            handlePOIClick(id);
          }}
          handleTaskStartClick={(id: number | string) => {
            handleTaskStartClick(id);
          }}
          handleTaskSendClick={(id: number | string) => {
            handleTaskSendClick(id);
          }}
          roleType={state.type}
          selectedRoleTaskStarts={state.selectedRoleTaskStarts}
          selectedRoleTaskSends={state.selectedRoleTaskSends}
          selectedPOIs={state.selectedPOIs}
        />
      ),
    },
  ];

  const handleTabChange = (newValue: number) => {
    setState({ ...state, tabValue: newValue });
  };

  const getRndInteger = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min)) + min;
  };

  const generateRandomHSL = (type: RoleTypeEnum) => {
    let colourCode = 120;
    switch (type) {
      case RoleTypeEnum.PATROL:
        colourCode = 0;
        break;
      case RoleTypeEnum.STATION:
        colourCode = 120;
        break;
      case RoleTypeEnum.ROAM:
        colourCode = 60;
        break;
    }
    let s = getRndInteger(30, 100);
    let l = getRndInteger(30, 80);

    const hslStringToReturn = "hsl(" + colourCode + "," + s + "%," + l + "%)";

    return hslStringToReturn;
  };

  const getFloorId = (floorNumber: number) => {
    const floor = data!.FloorOverlay.find((floor) => {
      return floor.FloorNumber === floorNumber;
    });
    return floor?.Id;
  };

  const onSubmit = async () => {
    const rolePois = [];
    const roleTaskSends = [];
    const roleTaskStarts = [];
    for (let i = 0; i < state.selectedPOIs.length; i++) {
      rolePois.push({
        POIId: state.selectedPOIs[i],
        IsActive: true,
      });
    }
    for (let i = 0; i < state.selectedRoleTaskSends.length; i++) {
      roleTaskSends.push({
        isActive: true,
        TaskId: state.selectedRoleTaskSends[i],
      });
    }
    for (let i = 0; i < state.selectedRoleTaskStarts.length; i++) {
      roleTaskStarts.push({
        isActive: true,
        TaskId: state.selectedRoleTaskStarts[i],
      });
    }
    try {
      const response = await insertRole({
        variables: {
          BuildingId: currentBuilding?.Id,
          ColourCode: generateRandomHSL(state.type),
          Indoors: state.trackingType === "Indoor",
          IsChargingStation: false,
          Lat: state.lat,
          Lng: state.lng,
          Title: state.name,
          Type: state.type,
          floorId: getFloorId(state.floorNumber),
          rolePois,
          roleTaskSends,
          roleTaskStarts,
        },
      });
    } catch (e) {
      console.log("🚀 ~ file: roles.tsx ~ line 236 ~ onSubmit ~ e", e);
      //TODO: add toast
    }
  };
  const list: JSX.Element = <RolesTable />;
  const add: JSX.Element = (
    <StepperFrame stepFrames={stepFrames} handleSubmit={onSubmit} />
  );
  return (
    <Page title="Roles">
      <ListAndAddTabs
        value={state.tabValue}
        handleChange={handleTabChange}
        listChildren={list}
        addChildren={add}
        fromEdit={state.editing}
      />
    </Page>
  );
}
